import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, input, inject } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatInput, MatFormField, MatLabel } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { ObjetMaquetteService } from '@app/features/editeur/services/objet-maquette.service';
import { IfIsAuthorized, IfIsNotAuthorized } from '@app/shared/autorisations/autorisations.decorator.ts';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { Observable, Subject } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

interface Perimetre {
    code: string;
    libelle: string;
}

export const PERIMETRE_ADMIN = 'PER-ADM';

@Component({
    selector: 'app-perimetres',
    templateUrl: './perimetres.component.html',
    styleUrls: ['./perimetres.component.scss'],
    imports: [
        MatCardTitle,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatChipGrid,
        MatChipRow,
        MatTooltip,
        MatChipRemove,
        MatIcon,
        MatInput,
        MatChipInput,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        MatButton,
        AsyncPipe
    ]
})
export class PerimetresComponent implements OnInit, OnDestroy {
    private _fb = inject(NonNullableFormBuilder);
    private _objetMaquetteService = inject(ObjetMaquetteService);
    private _notificationService = inject(NotificationService);
    private _autorisationsService = inject(AutorisationsService);
    private dialogService = inject(DialogService);
    private liveAnnouncer = inject(LiveAnnouncer);
    private cd = inject(ChangeDetectorRef);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    separatorKeysCodes: number[] = [ENTER, COMMA];
    private destroy$ = new Subject<void>();
    readonly objetMaquette = input.required<ObjetMaquette>();
    @ViewChild('perimetresInput') perimetresInput: ElementRef<MatInput>;

    mesPerimetres$: Observable<Perimetre[]> = this._autorisationsService.mesPerimetres$().pipe(
        tap(() => {
            this.cd.detectChanges();
        })
    );
    perimetresAjoutables$: Observable<Perimetre[]>;
    private tousLesCodesPerimetresSurObjetMaquette: string[] = [];
    private perimetresEnregistresEnBase: Perimetre[] = [];
    selectedPerimetres: Perimetre[] = [];
    form = this._fb.control<string>('');

    public readonly = false;
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    ngOnInit(): void {
        this.enableModificationIfAuthorized();
        this.disableModificationIfNotAuthorized();
    }

    private loadPerimetres() {
        this._objetMaquetteService
            .lireObjetMaquettePerimetres(this.objetMaquette().id!)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(codes => {
                    this.tousLesCodesPerimetresSurObjetMaquette = codes;
                    return this.mesPerimetres$.pipe(map(l => l.filter(p => codes.includes(p.code))));
                })
            )
            .subscribe((perimetresRecus: Perimetre[]) => {
                this.perimetresEnregistresEnBase = perimetresRecus;
                this.selectedPerimetres = perimetresRecus;
                this.setFiltering();
            });
    }

    private setFiltering() {
        this.perimetresAjoutables$ = this.form.valueChanges.pipe(
            startWith(''),
            filter(v => typeof v === 'string'),
            map(v => v.toLowerCase()),
            switchMap((recherche: string) =>
                this.mesPerimetres$.pipe(
                    map(l => l.filter(p => p.code !== PERIMETRE_ADMIN)),
                    map(l => l.filter(p => !this.selectedPerimetres.some(sp => sp.code === p.code))),
                    map(l => l.filter(p => p.code.toLowerCase().includes(recherche) || p.libelle.toLowerCase().includes(recherche)))
                )
            )
        );
    }

    @IfIsNotAuthorized(Traitement.ODF_ASSOCIER_OBJET_MAQUETTE_PERIMETRE)
    private disableModificationIfNotAuthorized() {
        this.readonly = true;
        this.form.disable();
    }

    @IfIsAuthorized(Traitement.ODF_ASSOCIER_OBJET_MAQUETTE_PERIMETRE)
    private enableModificationIfAuthorized() {
        this.loadPerimetres();
        this.setFiltering();
    }

    public enregistrerHandler() {
        if (this.tousLesPerimetresVontEtreRetires) {
            this.dialogService
                .openConfirmationDialog({
                    titre: `Veuillez confirmer`,
                    messageTexte: [
                        'Vous allez dissocier le dernier de vos périmètres associés à la Formation.',
                        'Si vous confirmez, cette formation ne sera plus visible pour vous.',
                        'Seul un utilisateur ayant les droits pourra visualiser et modifier cette formation.',
                        'Souhaitez-vous continuer ?'
                    ]
                })
                .pipe(takeUntil(this.destroy$))
                .subscribe((confirmation: boolean) => {
                    if (confirmation) {
                        this.enregistrer();
                    } else {
                        this.reset();
                    }
                });
        } else {
            this.enregistrer();
        }
    }

    private enregistrer() {
        this._objetMaquetteService
            .associerEtDissocierPerimetresFormation(
                this.objetMaquette().id!,
                this.perimetresAjoutes().map(p => p.code),
                this.perimetresRetires().map(p => p.code)
            )
            .subscribe({
                next: _ => {
                    console.log('_.perimetres', _.perimetres);
                },
                complete: () => {
                    this.loadPerimetres();
                    this.form.markAsPristine();
                    this._notificationService.success(`Les périmètres associés à la formation ont été bien enregistrés.`);
                    if (this.tousLesPerimetresVontEtreRetires) {
                        this.router.navigate(['../../'], { relativeTo: this.route });
                    }
                }
            });
    }

    remove(perimetre: Perimetre) {
        this.selectedPerimetres = [...this.selectedPerimetres.filter(p => p.code !== perimetre.code)];
        this.form.setValue('');
        this.form.markAsDirty();
        this.liveAnnouncer.announce(`Le périmètre ${perimetre.libelle}, de code ${perimetre.code}, a été retiré.`);
    }

    add(e: MatChipInputEvent) {
        // Clear the input value
        e.chipInput!.clear();
    }
    selected(e: MatAutocompleteSelectedEvent) {
        this.selectedPerimetres = [...this.selectedPerimetres, e.option.value];
        this.form.setValue('');
        this.perimetresInput.nativeElement.value = '';
    }

    perimetresAjoutes(): Perimetre[] {
        return this.selectedPerimetres.filter(p => !this.perimetresEnregistresEnBase.includes(p));
    }

    perimetresRetires(): Perimetre[] {
        return this.perimetresEnregistresEnBase.filter(p => !this.selectedPerimetres.includes(p));
    }

    get tousLesPerimetresVontEtreRetires(): boolean {
        return this.selectedPerimetres.length === 0 && this.perimetresEnLectureSeule.length !== 0;
    }

    get perimetresEnLectureSeule(): string[] {
        return this.tousLesCodesPerimetresSurObjetMaquette.filter(code => !this.perimetresEnregistresEnBase?.some(q => q.code === code));
    }

    private reset() {
        this.form.reset();
        this.loadPerimetres();
    }
}
